import React, {useCallback, useContext, useMemo} from 'react';
import {Button, IconButton, Stack, TableCell, Tooltip, Typography} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import PublicIcon from '@mui/icons-material/Public';
import {GlobalKeyVault, Office, UserRole} from "../../api/Model";
import {useRoleStore} from "../../api/RoleStore";
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {DataTable} from "../../components/data-table/DataTable";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {UserRoleDialog} from "./UserRoleDialog";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {useRemoteValue} from "../../util/RemoteValue";
import {useGlobalKeyVaultStore} from "../../api/GlobalKeyVaultStore";
import {OfficeIcon} from "../../components/office-icon/OfficeIcon";
import {KeyVaultChip} from "../../components/key-vault-chip/KeyVaultChip";
import {UserRoles_Admin} from "../../util/access/Permission";
import {usePermission} from "../../util/access/UseAccess";
import {useFilter} from "../../util/FilterSerializer";
import {filterUserRoles, roleDisplayName, UserRolesFilter, userRolesFilterSerializer} from "./UserRolesFilter";
import {Principal} from "../../components/principal/Principal";

function OfficeColumn({userRole}: { userRole: UserRole }) {
    const context = useContext(KeyTableActionContext);
    if (!context) throw new Error('TableActionContext not found');

    const {displayName, icon} = useMemo(() => {
        const role = userRole.role;
        switch (role.type) {
            case 'guest':
                return {displayName: 'None', icon: <BlockIcon color="disabled"/>};
            case 'system-admin':
                return {displayName: 'All', icon: <PublicIcon color="secondary"/>};
            case 'office-admin':
                return {
                    displayName: context.offices.find(office => office.id === role.officeId)?.name || 'n/a',
                    icon: <OfficeIcon officeId={role.officeId} colored/>,
                };
            case 'vault-manager':
                return {
                    displayName: context.offices.find(office => office.id === role.officeId)?.name || 'n/a',
                    icon: <OfficeIcon officeId={role.officeId} colored/>,
                };
        }
    }, [userRole, context.offices]);

    return (
        <TableCell>
            <Stack direction="row" gap={1} alignItems="center">
                {icon}
                <Typography>{displayName}</Typography>
            </Stack>
        </TableCell>
    );
}

function KeyVaultColumn({userRole}: { userRole: UserRole }) {
    const context = useContext(KeyTableActionContext);
    if (!context) throw new Error('TableActionContext not found');

    const keyVault: GlobalKeyVault | undefined = useMemo(() => {
        const role = userRole.role;
        switch (role.type) {
            case 'guest':
            case 'system-admin':
            case 'office-admin':
                return undefined;
            case 'vault-manager':
                return context.keyVaults.find(vault => vault.id === role.vaultId);
        }
    }, [userRole, context.keyVaults]);

    const displayName: string | undefined = useMemo(() => {
        const role = userRole.role;
        switch (role.type) {
            case 'guest':
                return 'None';
            case 'system-admin':
                return 'All';
            case 'office-admin':
                return 'All';
            case 'vault-manager':
                return undefined;
        }
    }, [userRole]);

    return (
        <TableCell>
            <Stack direction="row" gap={1} alignItems="center">
                {keyVault != null ? (
                    <KeyVaultChip keyVault={keyVault} size="small"/>
                ) : (
                    <Typography>{displayName}</Typography>
                )}
            </Stack>
        </TableCell>
    );
}

function RoleColumn({userRole}: { userRole: UserRole }) {
    const displayName: string = useMemo(() => roleDisplayName(userRole.role), [userRole]);

    return (
        <TableCell>
            <Typography>{displayName}</Typography>
        </TableCell>
    );
}

function ActionColumn({role}: { role: UserRole }) {
    const context = useContext(KeyTableActionContext);
    if (!context) throw new Error('TableActionContext not found');
    const {onEditRole, onDeleteRole} = context;
    const {canAdmin} = usePermission({canAdmin: UserRoles_Admin});

    const handleEdit = useCallback(() => {
        onEditRole(role);
    }, [onEditRole, role]);

    const handleDelete = useCallback(() => {
        onDeleteRole(role);
    }, [onDeleteRole, role]);

    return canAdmin ? (
        <TableCell align="right">
            <Tooltip title="Edit">
                <IconButton
                    color="primary"
                    onClick={handleEdit}
                >
                    <EditIcon/>
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton
                    color="error"
                    onClick={handleDelete}
                >
                    <DeleteIcon/>
                </IconButton>
            </Tooltip>
        </TableCell>
    ) : <TableCell/>;
}

const columns: TableColumnData<UserRole>[] = [
    {
        key: 'email',
        header: 'Email',
        cell: role => (
            <Tooltip title={role.email} arrow placement="top">
                <TableCell>
                    <Principal principalEmail={role.email}/>
                </TableCell>
            </Tooltip>
        ),
    },
    {
        key: 'role',
        header: 'Role',
        width: '16em',
        cell: (role) => (
            <RoleColumn userRole={role}/>
        ),
    },
    {
        key: 'office',
        header: 'Office',
        width: '15em',
        cell: role => <OfficeColumn userRole={role}/>,
    },
    {
        key: 'keyVault',
        header: 'Key Vault',
        width: '15em',
        cell: role => <KeyVaultColumn userRole={role}/>,
    },
    {
        key: 'actions',
        header: 'Actions',
        width: '12em',
        align: 'right',
        cell: (role) => <ActionColumn role={role}/>,
    },
]

type KeyTableActions = {
    onEditRole: (row: UserRole) => void;
    onDeleteRole: (row: UserRole) => void;
    offices: Office[];
    keyVaults: GlobalKeyVault[];
}

const KeyTableActionContext = React.createContext<KeyTableActions | undefined>(undefined);


export default function UserRolesPage({offices}: {
    offices: Office[],
}) {
    const {
        remoteValue: roles,
        createRole,
        updateRole,
        deleteRole,
    } = useRoleStore();

    const keyVaultStore = useGlobalKeyVaultStore();
    const keyVaults = useRemoteValue(keyVaultStore.remoteValue, []);
    const {canAdmin} = usePermission({canAdmin: UserRoles_Admin});
    const filterSerializer = useMemo(
        () => userRolesFilterSerializer(offices),
        [offices]
    );
    const [filter, setFilter] = useFilter(filterSerializer);

    const filteredRoles = useMemo(
        () => roles.map(roles => filterUserRoles(roles, filter, offices, keyVaults)),
        [roles, filter, offices, keyVaults],
    );

    const handleCreateRole = useFormDialogAction({
        title: 'Create Role',
        description: 'Enter the details for the new user role.',
        submitButtonText: 'Create',
        renderForm: UserRoleDialog,
        action: (openDialog) => openDialog({
            initialValue: {email: '', role: {type: 'guest'}},
            onSubmit: createRole,
            successMessage: form => `Created new role for ${form.email}`,
            errorMessage: 'Failed to create new role. Please try again later.',
        })
    }, [createRole]);

    const handleUpdateRole = useFormDialogAction({
        title: 'Edit Role',
        description: 'Update the user role information below.',
        submitButtonText: 'Update',
        renderForm: UserRoleDialog,
        action: (openDialog, role: UserRole) => openDialog({
            initialValue: {
                email: role.email,
                role: role.role,
            },
            onSubmit: form => updateRole(role, form),
            successMessage: form => `Updated role for ${form.email}`,
            errorMessage: 'Failed to update role. Please try again later.',
        })
    }, [updateRole]);

    const handleDeleteRole = useDeleteAction({
        description: 'Are you sure you want to delete this role? This action cannot be undone.',
        action: deleteRole,
        successMessage: role => `Deleted role ${role.email}`,
        failureMessage: role => `Failed to delete role ${role.email}`,
    }, [deleteRole]);

    return (
        <Page
            header="User Roles"
            counter={<StorePageCount totalValue={roles} value={filteredRoles}/>}
            description="Manage user roles and permissions."
            filter={<UserRolesFilter filter={filter} setFilter={setFilter}/>}
            actions={canAdmin && (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon/>}
                    onClick={handleCreateRole}
                >
                    Add Role
                </Button>
            )}>

            <LoadingData
                value={filteredRoles}
                emptyMessage="No user roles found."
                defaultErrorMessage="Failed to load roles. Please try again later."
                children={roles => (
                    <KeyTableActionContext value={{
                        offices,
                        keyVaults,
                        onEditRole: handleUpdateRole,
                        onDeleteRole: handleDeleteRole,
                    }}>
                        <DataTable
                            columns={columns}
                            rows={roles}
                            id={role => role.id}
                        />
                    </KeyTableActionContext>
                )}
            />
        </Page>
    );
};
